import { useEffect, type ReactNode } from 'react';

/**
 * Generic modal using the prototype's ported classes (.modal-back / .modal)
 * and its dismiss behaviour: clicking the backdrop closes (index.html:731).
 * Escape-to-close is added — the prototype had no keyboard dismiss, which is
 * an a11y gap rather than a design decision.
 *
 * Pass `header` / `footer` to keep them sticky while only the body scrolls.
 */
export function Modal({
  onClose,
  label,
  children,
  wide = false,
  header,
  footer,
}: {
  onClose: () => void;
  label: string;
  children: ReactNode;
  wide?: boolean;
  header?: ReactNode;
  footer?: ReactNode;
}) {
  const stickyLayout = header != null || footer != null;

  useEffect(() => {
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center bg-[rgba(20,20,20,.55)] p-5 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className={`relative flex max-h-[92vh] w-full flex-col rounded-[18px] bg-[var(--card)] shadow-[var(--shadow-lg)] [animation:pop_.25s_ease] ${
          stickyLayout ? 'overflow-hidden' : 'overflow-y-auto p-[30px]'
        } ${wide ? 'max-w-[760px]' : 'max-w-[430px]'}`}
        role="dialog"
        aria-modal="true"
        aria-label={label}
      >
        {header ? <div className="shrink-0 border-b border-[var(--line)] px-[30px] pt-[26px] pb-4">{header}</div> : null}
        <div className={stickyLayout ? 'min-h-0 flex-1 overflow-y-auto px-[30px] py-4' : undefined}>{children}</div>
        {footer ? (
          <div className="shrink-0 border-t border-[var(--line)] bg-[var(--card)] px-[30px] py-4">{footer}</div>
        ) : null}
      </div>
    </div>
  );
}
